// The one place the portal turns API date/time values into Dates.
//
// Two kinds of value come from the API and they must never be mixed up:
//
// 1. Timestamps: an instant ("created_at", "call_started_at", ...). Many DB
//    columns are naive and hold UTC, and older endpoints send them without an
//    offset ("2026-10-15T19:17:00"). JS `new Date()` / date-fns `parseISO()`
//    read such a string as LOCAL time, which shows a UTC time as if it were
//    local (5 hours ahead in CDT). `parseApiTimestamp` treats a string without
//    an offset as UTC; display then happens in the viewer's local time zone.
//
// 2. Calendar dates: a day, not an instant (date of birth, due dates, date of
//    service, summary month). `new Date("1941-09-05")` is UTC midnight, which is
//    Sep 4 in any US time zone. `parseDateOnly` builds a local-midnight Date for
//    that calendar day, so it never shifts.
//
// Do not call `new Date(apiString)` or `parseISO(apiString)` in components; use
// these helpers.

import { format as fnsFormat } from "date-fns"

export type ApiDateValue = string | number | Date | null | undefined

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/
const DATE_PREFIX = /^(\d{4})-(\d{2})-(\d{2})/
const DATE_TIME = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}(?::\d{2})?)(\.\d+)?(.*)$/
const OFFSET = /^(Z|[+-]\d{2}(?::?\d{2})?)$/i

function validOrNull(d: Date): Date | null {
  return Number.isNaN(d.getTime()) ? null : d
}

/**
 * Parse a timestamp from the API as a UTC instant.
 *
 * - "2026-10-15T19:17:00" (no offset) is read as UTC.
 * - "2026-10-15T19:17:00Z" / "...+00:00" / "...-05:00" keep their offset.
 * - A bare date ("2026-10-15") is read as UTC midnight; use `parseDateOnly`
 *   for calendar dates instead.
 * Returns null for empty or unparseable input.
 */
export function parseApiTimestamp(value: ApiDateValue): Date | null {
  if (value === null || value === undefined || value === "") return null
  if (value instanceof Date) return validOrNull(value)
  if (typeof value === "number") return validOrNull(new Date(value))
  const s = value.trim()
  const m = DATE_TIME.exec(s)
  if (m) {
    const [, day, clock, fraction = "", rest] = m
    const offset = rest.trim()
    if (offset && !OFFSET.test(offset)) return null
    // Python sends microseconds; keep milliseconds so every browser parses it.
    const ms = fraction ? fraction.slice(0, 4).padEnd(4, "0") : ""
    const tz = offset ? (offset.toUpperCase() === "Z" ? "Z" : normalizeOffset(offset)) : "Z"
    return validOrNull(new Date(`${day}T${clock.length === 5 ? `${clock}:00` : clock}${ms}${tz}`))
  }
  if (DATE_ONLY.test(s)) return validOrNull(new Date(`${s}T00:00:00Z`))
  return validOrNull(new Date(s))
}

function normalizeOffset(offset: string): string {
  // "+0530" -> "+05:30", "+05" -> "+05:00"
  const sign = offset[0]
  const digits = offset.slice(1).replace(":", "")
  return `${sign}${digits.slice(0, 2)}:${(digits.slice(2) || "00").padEnd(2, "0")}`
}

/** Format an API timestamp in the viewer's local time zone (date-fns pattern). */
export function formatTimestamp(
  value: ApiDateValue,
  pattern = "MMM d, yyyy h:mm a",
  fallback = ""
): string {
  const d = parseApiTimestamp(value)
  return d ? fnsFormat(d, pattern) : fallback
}

/** Milliseconds since epoch for an API timestamp, or NaN. For sorting/diffs. */
export function timestampMs(value: ApiDateValue): number {
  return parseApiTimestamp(value)?.getTime() ?? Number.NaN
}

/**
 * Parse a calendar date ("1941-09-05", or the date part of "1941-09-05T00:00:00")
 * as local midnight of that day. It never shifts across time zones.
 * Returns null for empty or unparseable input.
 */
export function parseDateOnly(value: ApiDateValue): Date | null {
  if (value === null || value === undefined || value === "") return null
  if (value instanceof Date) {
    return validOrNull(new Date(value.getFullYear(), value.getMonth(), value.getDate()))
  }
  if (typeof value === "number") return null
  const m = DATE_PREFIX.exec(value.trim())
  if (!m) return null
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])]
  const date = new Date(y, mo - 1, d)
  // Reject impossible dates such as 2026-02-31 instead of rolling them over.
  if (date.getFullYear() !== y || date.getMonth() !== mo - 1 || date.getDate() !== d) return null
  return date
}

/** Format a calendar date without any time-zone shift (date-fns pattern). */
export function formatDateOnly(value: ApiDateValue, pattern = "MMM d, yyyy", fallback = ""): string {
  const d = parseDateOnly(value)
  return d ? fnsFormat(d, pattern) : fallback
}

/**
 * A local Date's calendar day as "YYYY-MM-DD", for sending a date-only value
 * to the API. (`date.toISOString().split("T")[0]` is the UTC day, which is the
 * wrong day for part of every day in any non-UTC zone.)
 */
export function toDateOnlyString(date: Date = new Date()): string {
  return fnsFormat(date, "yyyy-MM-dd")
}

/** Whole years between a date of birth and `now` (local calendar). */
export function ageFromDob(dob: ApiDateValue, now: Date = new Date()): number | null {
  const birth = parseDateOnly(dob)
  if (!birth) return null
  let age = now.getFullYear() - birth.getFullYear()
  const monthDiff = now.getMonth() - birth.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < birth.getDate())) age--
  return age
}
